<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import InternalChatRecipients from './InternalChatRecipients.vue';

const props = defineProps({
  mode: { type: String, required: true },
  agents: { type: Array, default: () => [] },
  initialUserIds: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  performAction: { type: Function, required: true },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const dialog = ref(null);
let unmounting = false;
const onClose = () => {
  if (!unmounting) emit('close');
};
const name = ref('');
const selectedAgentIds = ref([...props.initialUserIds]);
const validationError = ref('');
onMounted(() => dialog.value.open());
onBeforeUnmount(() => {
  unmounting = true;
  dialog.value.close();
});
const create = async () => {
  if (props.isLoading) return;
  validationError.value = '';
  if (props.mode !== 'dm' && !name.value.trim()) {
    validationError.value = t('INTERNAL_CHAT.CHANNEL_NAME_REQUIRED');
    return;
  }
  if (props.mode === 'dm' && !selectedAgentIds.value.length) {
    validationError.value = t('INTERNAL_CHAT.DM_RECIPIENT_REQUIRED');
    return;
  }
  await props.performAction('create', {
    channelType: props.mode,
    name: name.value.trim(),
    userIds: props.mode === 'dm' ? [...selectedAgentIds.value] : [],
  });
};
</script>

<template>
  <Dialog
    ref="dialog"
    width="md"
    :title="t(`INTERNAL_CHAT.CREATE_${mode.toUpperCase()}`)"
    :confirm-button-label="t('INTERNAL_CHAT.CREATE')"
    :cancel-button-label="t('INTERNAL_CHAT.CANCEL')"
    :is-loading="isLoading"
    :confirm-before-close="isLoading"
    @confirm="create"
    @close="onClose"
  >
    <Input
      v-if="mode !== 'dm'"
      v-model="name"
      :disabled="isLoading"
      :label="t('INTERNAL_CHAT.CHANNEL_NAME')"
      :placeholder="t('INTERNAL_CHAT.CHANNEL_NAME_PLACEHOLDER')"
      autofocus
    />
    <div v-else>
      <InternalChatRecipients
        v-model="selectedAgentIds"
        :agents="agents"
        :disabled="isLoading"
      />
      <p class="mt-2 mb-0 text-xs text-n-slate-11">
        {{ t('INTERNAL_CHAT.DM_FIXED_PARTICIPANTS') }}
      </p>
    </div>
    <p
      v-if="validationError || error"
      class="m-0 text-sm text-n-ruby-11"
      role="alert"
    >
      {{ validationError || error }}
    </p>
  </Dialog>
</template>
